import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Alert, message } from 'antd';
import { authAxios } from '../utils/session_utils';
import { StreamingChannel } from './types';

const { Option } = Select;

interface ChannelFormProps {
  channel: StreamingChannel | null;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

/**
 * Derive a channel id from a display name: fold accents, lowercase, and join
 * words with single hyphens ("Nosey Escándalos" -> "nosey-escandalos").
 *
 * A SUGGESTION only — the field stays editable until the channel is created.
 * The id is deliberately not kept in sync with the name afterwards: the name is
 * a label anyone may reword, while the id is what partners key their ingest to,
 * so tying them together would make every cosmetic rename a breaking re-key.
 */
export function slugify(name: string): string {
  return name
    .normalize('NFD')                    // split accented chars into base + mark
    .replace(/[\u0300-\u036f]/g, '')   // drop the combining marks (á -> a)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')         // any run of non-slug chars becomes one hyphen
    .replace(/^-+|-+$/g, '');            // no leading/trailing hyphens
}

/** Must match CHANNEL_ID_PATTERN in the BE's channels routes. */
const CHANNEL_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Form value for the default EPG profile; sent to the BE as '' (stored as NULL). */
const DEFAULT_EPG_PROFILE = 'default';

const ChannelForm: React.FC<ChannelFormProps> = ({ channel, open, onClose, onSave }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Once the user edits the id by hand, stop overwriting it from the name.
  const [idTouched, setIdTouched] = useState(false);

  const isEditMode = !!channel;

  useEffect(() => {
    if (!open) return;
    setIdTouched(false);
    if (channel) {
      form.setFieldsValue({
        display_name: channel.display_name,
        id: channel.id,
        language: channel.language,
        logo_url: channel.logo_url,
        cover_url: channel.cover_url,
        poster_url: channel.poster_url,
        epg_profile: channel.epg_profile || DEFAULT_EPG_PROFILE,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ epg_profile: DEFAULT_EPG_PROFILE });
    }
  }, [open, channel, form]);

  const epgProfile = Form.useWatch('epg_profile', form);
  const isAmazon = epgProfile === 'amazon';

  // Prefill the id from the name while creating, until the user takes it over.
  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditMode || idTouched) return;
    form.setFieldsValue({ id: slugify(e.target.value) });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const editable = {
        display_name: values.display_name,
        language: values.language || '',
        logo_url: values.logo_url || '',
        cover_url: values.cover_url || '',
        poster_url: values.poster_url || '',
        epg_profile: values.epg_profile === DEFAULT_EPG_PROFILE ? '' : values.epg_profile || '',
      };

      if (isEditMode && channel) {
        // `id` is never sent on update — the BE rejects any attempt to change it.
        await authAxios({
          method: 'PUT',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/channels/${channel.id}`,
          data: editable,
        });
        message.success('Channel updated');
      } else {
        await authAxios({
          method: 'POST',
          url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/channels`,
          data: { id: values.id, ...editable },
        });
        message.success('Channel created');
      }
      onSave();
    } catch (error: any) {
      // A failed validateFields() rejects too; only report real request errors.
      if (error?.errorFields) return;
      console.error('Error saving channel:', error);
      message.error(error?.response?.data?.error || 'Failed to save channel');
    } finally {
      setLoading(false);
    }
  };

  const validateChannelId = (_: any, value: string) => {
    if (isEditMode) return Promise.resolve(); // disabled field; never submitted
    if (!value) return Promise.reject(new Error('Channel id is required'));
    if (!CHANNEL_ID_PATTERN.test(value)) {
      return Promise.reject(
        new Error('Use lowercase letters, digits and single hyphens, e.g. "nosey-escandalos"')
      );
    }
    return Promise.resolve();
  };

  const validateUrl = (_: any, value: string) => {
    if (!value) return Promise.resolve();
    try {
      const url = new URL(value);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return Promise.reject(new Error('URL must use http:// or https://'));
      }
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error('Please enter a valid URL'));
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Channel' : 'Add Channel'}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      okText={isEditMode ? 'Update' : 'Create'}
      width={640}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="display_name"
          label="Display Name"
          tooltip="The viewer-facing channel name, shown in the EPG guide."
          rules={[{ required: true, message: 'Display name is required' }]}
        >
          <Input placeholder="e.g. Nosey Escándalos" onChange={handleDisplayNameChange} />
        </Form.Item>

        <Form.Item
          name="id"
          label="Channel ID"
          tooltip="The channel's permanent identifier, published in the EPG. Partners key their ingest configuration to this, so it can never be changed once the channel is created."
          rules={[{ validator: validateChannelId }]}
          extra={
            isEditMode
              ? 'Permanent — a channel id can never be changed.'
              : 'Suggested from the display name. Edit it now if needed: it is permanent once created.'
          }
        >
          <Input
            placeholder="e.g. nosey-escandalos"
            disabled={isEditMode}
            onChange={() => setIdTouched(true)}
          />
        </Form.Item>

        {!isEditMode && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="The Channel ID is permanent"
            description="It is published in the EPG as the channel's identity, and partners configure their ingest against it. It cannot be changed or deleted afterwards — every other field can."
          />
        )}

        <Form.Item
          name="language"
          label="Language"
          tooltip="The language the channel broadcasts in. Labels the titles and descriptions in the EPG. Leave blank for English."
        >
          <Select placeholder="Select language" allowClear>
            <Option value="english">English</Option>
            <Option value="spanish">Spanish</Option>
            <Option value="portuguese">Portuguese</Option>
            <Option value="german">German</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="logo_url"
          label="Logo URL"
          tooltip="The channel's logo, published in the EPG next to the channel. Must be publicly reachable — partners fetch it directly."
          rules={[{ validator: validateUrl }]}
          extra={isAmazon ? 'Prime Video: 16:9 PNG, 1920x1080, mono-white (#FFFFFF) logo on a fully transparent background.' : undefined}
        >
          <Input placeholder="https://…/logo.png" />
        </Form.Item>

        <Form.Item
          name="epg_profile"
          label="EPG Profile"
          tooltip="Which fields this channel's EPG carries. Amazon adds the Prime Video linear fields (program and season ids, genre, release year, run length, artwork roles, Off Air entries, channel cover and poster). Changes take effect the next time the stream's playlist is generated."
        >
          <Select>
            <Option value={DEFAULT_EPG_PROFILE}>Default</Option>
            <Option value="amazon">Amazon (Prime Video via Amagi)</Option>
          </Select>
        </Form.Item>

        {/* Hidden (not unmounted) on the default profile, so switching profile
            never drops saved art from what gets submitted. */}
        <Form.Item
          hidden={!isAmazon}
          name="cover_url"
          label="Cover Art URL"
          tooltip="Colorful artwork with the channel logo prominently displayed, used in Prime Video carousels. Must be publicly reachable."
          rules={[{ validator: validateUrl }]}
          extra="16:9, 3840x2160, JPG or PNG, max 10 MB. Readable logo centred on a designed backdrop (not flat black), no people, not transparent. Safe zones: top 320, bottom 480, sides 120 px."
        >
          <Input placeholder="https://…/cover.jpg" />
        </Form.Item>

        <Form.Item
          hidden={!isAmazon}
          name="poster_url"
          label="Poster Art URL"
          tooltip="Poster version of the cover art; Prime Video's mobile fallback when a programme has no poster. Must be publicly reachable."
          rules={[{ validator: validateUrl }]}
          extra="2:3, 2000x3000, JPG or PNG, max 10 MB. Same rules as the cover. Safe zones: top and bottom 240, sides 80 px."
        >
          <Input placeholder="https://…/poster.jpg" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChannelForm;
